import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Paper,
  Grid,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Button,
  Chip,
} from '@mui/material';
import { useAuth } from '../../../contexts/AuthContext';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import RefreshIcon from '@mui/icons-material/Refresh';

interface RelayState {
  relayNumber: number;
  state: number;
  label: string;
}

interface RelayControlTabProps {
  deviceId: string;
}

const RelayControlTab: React.FC<RelayControlTabProps> = ({ deviceId }) => {
  const auth = useAuth();
  const accessToken = auth.tokens?.accessToken;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [relayStates, setRelayStates] = useState<RelayState[]>([]);
  const [updating, setUpdating] = useState<number | null>(null);
  
  const authHeader = useMemo(() => ({ 
    'Authorization': `Bearer ${accessToken}`,
    'Content-Type': 'application/json'
  }), [accessToken]);

  const loadRelayStates = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch device settings to get relay count and current states
      const res = await fetch(`/api/devices/${deviceId}/settings`, { 
        headers: authHeader 
      });
      
      if (!res.ok) throw new Error('Failed to fetch device settings');
      
      const settings = await res.json();
      
      // Get relay count
      const relayCountSetting = settings.find((s: any) => s.settingName === 'device.relay-count');
      const relayCount = relayCountSetting ? parseInt(relayCountSetting.settingValue) : 2;
      
      // Build relay states array
      const states: RelayState[] = [];
      for (let i = 1; i <= relayCount; i++) {
        const relayKey = `r${i}`;
        const stateSetting = settings.find((s: any) => s.settingName === `relay.${relayKey}.state`);
        const labelSetting = settings.find((s: any) => s.settingName === `relay.${relayKey}.label`);
        
        states.push({
          relayNumber: i,
          state: stateSetting ? parseInt(stateSetting.settingValue) : 0,
          label: labelSetting?.settingValue || `Relay ${i}`,
        });
      }
      
      setRelayStates(states);
    } catch (e: any) {
      setError(e.message || 'Failed to load relay states');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRelayStates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId]);

  const handleToggleRelay = async (relayNumber: number, currentState: number) => {
    const newState = currentState === 1 ? 0 : 1;
    setUpdating(relayNumber);
    setError(null);
    setSuccess(null);
    
    try {
      const res = await fetch('/api/deviceactions/set-relay-state', {
        method: 'POST',
        headers: authHeader,
        body: JSON.stringify({
          deviceId,
          relayNumber,
          state: newState,
        }),
      });
      
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Failed to update relay state');
      }
      
      // Update local state
      setRelayStates((prev) =>
        prev.map((r) =>
          r.relayNumber === relayNumber ? { ...r, state: newState } : r
        )
      );
      
      setSuccess(`Relay ${relayNumber} turned ${newState === 1 ? 'ON' : 'OFF'}`);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (e: any) {
      setError(e.message || 'Failed to update relay state');
    } finally {
      setUpdating(null);
    }
  };

  const handleTurnAllOn = async () => {
    setError(null);
    setSuccess(null);
    
    for (const relay of relayStates) {
      if (relay.state !== 1) {
        await handleToggleRelay(relay.relayNumber, 0);
      }
    }
    
    setSuccess('All relays turned ON');
  };

  const handleTurnAllOff = async () => {
    setError(null);
    setSuccess(null);
    
    for (const relay of relayStates) {
      if (relay.state !== 0) {
        await handleToggleRelay(relay.relayNumber, 1);
      }
    }
    
    setSuccess('All relays turned OFF');
  };

  return (
    <Box>
      {/* Fixed notifications at top with fade-out */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            position: 'fixed',
            top: 80,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 9999,
            minWidth: '400px',
            maxWidth: '600px',
            boxShadow: 3,
            animation: 'fadeOut 0.5s ease-in 2.5s forwards',
            '@keyframes fadeOut': {
              from: { opacity: 1 },
              to: { opacity: 0 }
            }
          }} 
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {success && (
        <Alert 
          severity="success" 
          sx={{ 
            position: 'fixed',
            top: 80,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 9999,
            minWidth: '400px',
            maxWidth: '600px',
            boxShadow: 3,
            animation: 'fadeOut 0.5s ease-in 2.5s forwards',
            '@keyframes fadeOut': {
              from: { opacity: 1 },
              to: { opacity: 0 }
            }
          }} 
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: 2,
        mb: 3 
      }}>
        <Typography variant="h6">Relay Control</Typography>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 1 
        }}>
          <Button
            variant="outlined"
            color="success"
            size="small"
            onClick={handleTurnAllOn}
            disabled={loading || updating !== null}
            fullWidth
            sx={{ minWidth: { xs: 'auto', sm: '120px' } }}
          >
            All ON
          </Button>
          <Button
            variant="outlined"
            color="error"
            size="small"
            onClick={handleTurnAllOff}
            disabled={loading || updating !== null}
            fullWidth
            sx={{ minWidth: { xs: 'auto', sm: '120px' } }}
          >
            All OFF
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            size="small"
            onClick={loadRelayStates}
            disabled={loading}
            fullWidth
            sx={{ minWidth: { xs: 'auto', sm: '120px' } }}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      ) : relayStates.length === 0 ? (
        <Alert severity="info">
          No relay configuration found for this device. Please configure device.relay-count in device settings.
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {relayStates.map((relay) => (
            <Grid item xs={12} sm={6} md={4} key={relay.relayNumber}>
              <Card 
                sx={{ 
                  backgroundColor: relay.state === 1 ? 'rgba(16, 185, 129, 0.12)' : '#1a1f2e',
                  border: '1px solid',
                  borderColor: relay.state === 1 ? 'rgba(16, 185, 129, 0.5)' : '#2d3748',
                  transition: 'all 0.3s ease',
                  boxShadow: relay.state === 1 ? '0 0 12px rgba(16, 185, 129, 0.2)' : 'none',
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <PowerSettingsNewIcon 
                      sx={{ 
                        fontSize: 40, 
                        mr: 2,
                        color: relay.state === 1 ? '#10b981' : '#4b5563',
                        filter: relay.state === 1 ? 'drop-shadow(0 0 6px rgba(16,185,129,0.6))' : 'none',
                        transition: 'all 0.3s ease',
                      }} 
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" component="div" sx={{ color: 'text.primary' }}>
                        {relay.label}
                      </Typography>
                      <Chip 
                        label={relay.state === 1 ? 'ON' : 'OFF'}
                        size="small"
                        sx={{ 
                          mt: 0.5,
                          backgroundColor: relay.state === 1 ? 'rgba(16,185,129,0.2)' : 'rgba(75,85,99,0.3)',
                          color: relay.state === 1 ? '#10b981' : '#9ca3af',
                          fontWeight: 700,
                          letterSpacing: '0.05em',
                          border: '1px solid',
                          borderColor: relay.state === 1 ? 'rgba(16,185,129,0.4)' : 'rgba(75,85,99,0.4)',
                        }}
                      />
                    </Box>
                  </Box>
                  
                  <FormControlLabel
                    control={
                      <Switch
                        checked={relay.state === 1}
                        onChange={() => handleToggleRelay(relay.relayNumber, relay.state)}
                        disabled={updating === relay.relayNumber}
                        color="success"
                        sx={{
                          '& .MuiSwitch-track': {
                            backgroundColor: relay.state === 1 ? undefined : '#374151',
                          },
                        }}
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {updating === relay.relayNumber 
                          ? 'Updating...' 
                          : relay.state === 1 ? 'Turn OFF' : 'Turn ON'}
                      </Typography>
                    }
                  />
                  
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #2d3748', pt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Channel: {relay.relayNumber}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      State: {relay.state}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Paper sx={{ p: 2, mt: 3, backgroundColor: 'rgba(79,195,247,0.07)', border: '1px solid rgba(79,195,247,0.2)' }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          <strong style={{ color: '#4fc3f7' }}>Note:</strong> Relay devices will poll the server periodically to retrieve their commanded states. 
          Changes made here will be applied on the next device poll cycle.
        </Typography>
      </Paper>
    </Box>
  );
};

export default RelayControlTab;
